import { useCallback, useEffect, useState } from "react";
import { fetchTransferDetail } from "@/lib/api";
import type { TransferDetail } from "@/types";
import { MOCK_TRANSFER_DETAILS } from "@/data/mock";

interface UseTransferDetailResult {
  detail: TransferDetail | null;
  loading: boolean;
  error: string | null;
  source: "api" | "mock";
  refresh: () => void;
}

function mapApiTransferDetail(raw: Record<string, unknown>): TransferDetail {
  const id = String(raw.id ?? raw.transferId ?? "");
  const direction = String(raw.direction ?? "send") as TransferDetail["direction"];
  const amountMain = String(raw.amountMain ?? raw.amount ?? "0");
  const amountUsd = String(raw.amountUsd ?? raw.estimatedUsd ?? "");
  const source = String(raw.source ?? raw.sourceWallet ?? "");
  const destination = String(raw.destination ?? raw.destinationAddress ?? "");
  const createdBy = String(raw.createdBy ?? raw.creator ?? "");
  const providerRoute = String(raw.providerRoute ?? raw.provider ?? "");
  const defaultState = String(raw.state ?? raw.status ?? "draft") as TransferDetail["defaultState"];

  const timeline = Array.isArray(raw.timeline)
    ? (raw.timeline as Record<string, unknown>[]).map(e => ({
        title: String(e.title ?? ""),
        meta: typeof e.meta === "string" ? e.meta : undefined,
        detail: typeof e.detail === "string" ? e.detail : undefined,
        tone: (String(e.tone ?? "success")) as "success" | "active" | "danger" | "pending",
      }))
    : [];

  const evidence = Array.isArray(raw.evidence)
    ? (raw.evidence as Record<string, unknown>[]).map(e => ({
        id: String(e.id ?? ""),
        label: String(e.label ?? e.id ?? ""),
      }))
    : [];

  return {
    id, direction, amountMain, amountUsd, source, destination,
    createdBy, providerRoute, defaultState, timeline, evidence,
    policy: typeof raw.policy === "string" ? raw.policy : undefined,
  };
}

export function useTransferDetail(id: string): UseTransferDetailResult {
  const [detail, setDetail] = useState<TransferDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<"api" | "mock">("api");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchTransferDetail(id);
      if (result.transfer) {
        setDetail(mapApiTransferDetail(result.transfer));
        setSource("api");
      } else {
        const mock = MOCK_TRANSFER_DETAILS[id];
        if (mock) {
          setDetail(mock);
          setSource("mock");
        } else {
          setError(result.reasonCode || "Transfer not found");
          setDetail(null);
        }
      }
    } catch {
      const mock = MOCK_TRANSFER_DETAILS[id];
      if (mock) {
        setDetail(mock);
        setSource("mock");
      } else {
        setError("Failed to load transfer");
        setDetail(null);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  return { detail, loading, error, source, refresh: load };
}
